import React, { useState, useEffect } from 'react';
import {
  Dimensions,
  FlatList,
  Modal,
  ModalProps,
  Platform
} from 'react-native';
import styled from 'styled-components/native';

import { Feather, Ionicons } from '@expo/vector-icons';

import { StudyParticipantImage } from '../models/StudyParticipantImage';
import IconButton from './IconButton';
import ImageModal from './ImageModal';
import { B } from './Typography';
import Button from './Button';
import ImageCompareModal from './ImageCompareModal';
import {
  disableOrientationLock,
  enablePortraitOrientationLock
} from '../tools/DeviceHelper';
import elementIDs from '../../elementIDs';

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 52}px;
  left: 20px;
  z-index: 1;
`;

const StyledImageCheckbox = styled(Ionicons)`
  position: absolute;
  bottom: 4px;
  right: 4px;
`;

const StyledTitle = styled(B)`
  margin-top: ${Platform.OS === 'web' ? 16 : 42}px;
  text-align: center;
`;

const StyledToolbarView = styled.View`
  height: ${Platform.OS === 'web' ? 64 : 96}px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledTouchableOpacity = styled.TouchableOpacity`
  margin: 4px;
`;

const StyledImage = styled.Image`
  border-radius: 8px;
`;

const StyledCompareButton = styled(Button)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 48}px;
  right: 20px;
`;

const ImageGalleryModal: React.FC<
  ModalProps & {
    title: string;
    images: StudyParticipantImage[];
  }
> = ({ title, images, ...props }) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [selectedItems, setSelectedItems] = useState<StudyParticipantImage[]>(
    []
  );
  const { onRequestClose } = props;
  const [selectedImage, setSelectedImage] = useState<StudyParticipantImage>();
  const columnCount = 4;
  const cellPadding = 4;
  const screenWidth = Dimensions.get('window').width;
  const tileSize = screenWidth / columnCount - cellPadding * columnCount;

  useEffect(() => {
    if (!isSelecting) {
      setSelectedItems([]);
    }
  }, [isSelecting]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      disableOrientationLock();
    }
  }, []);

  return (
    <Modal
      animationType='slide'
      {...props}
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right'
      ]}
    >
      <StyledToolbarView>
        <StyledTitle>{title}</StyledTitle>
        <StyledCloseIconButton
          testID={elementIDs.pluginGalleryModalExitID}
          onPress={async e => {
            if (isSelecting) {
              setIsSelecting(false);
            } else {
              if (Platform.OS !== 'web') {
                await enablePortraitOrientationLock();
              }
              onRequestClose?.(e);
            }
          }}
        >
          <Feather
            name={isSelecting ? 'x' : 'arrow-left'}
            size={28}
            color='black'
          />
        </StyledCloseIconButton>
        <StyledCompareButton
          outlined={!isSelecting}
          disabled={isSelecting && selectedItems.length !== 2}
          size={'small'}
          onPress={() => {
            if (isSelecting && selectedItems.length === 2) {
              setIsComparing(true);
            } else {
              setIsSelecting(true);
            }
          }}
        >
          Compare
        </StyledCompareButton>
      </StyledToolbarView>
      <FlatList
        contentContainerStyle={{
          paddingHorizontal: 16
        }}
        data={images}
        renderItem={({ item }) => (
          <StyledTouchableOpacity
            activeOpacity={0.7}
            disabled={
              !selectedItems.includes(item) && selectedItems.length === 2
            }
            onPress={() => {
              if (isSelecting) {
                if (selectedItems.indexOf(item) === -1) {
                  setSelectedItems([...selectedItems, item]);
                } else {
                  const arr = selectedItems.filter(obj => obj !== item);
                  setSelectedItems(arr);
                }
              } else {
                setSelectedImage(item);
              }
            }}
          >
            <StyledImage
              source={{ uri: item.smallUrl }}
              resizeMode='cover'
              style={{ width: tileSize, height: tileSize }}
            />
            {isSelecting && (
              <StyledImageCheckbox
                size={18}
                name={
                  selectedItems.indexOf(item) !== -1
                    ? 'ios-checkmark-circle'
                    : 'radio-button-off'
                }
                color='white'
              />
            )}
          </StyledTouchableOpacity>
        )}
        //Setting the number of column
        numColumns={columnCount}
        keyExtractor={(item, index) => index.toString()}
      />
      {!!selectedImage && (
        <ImageModal
          supportedOrientations={[
            'portrait',
            'landscape',
            'landscape-left',
            'landscape-right'
          ]}
          image={selectedImage}
          onRequestClose={() => setSelectedImage(undefined)}
        />
      )}
      {isComparing && (
        <ImageCompareModal
          supportedOrientations={[
            'portrait',
            'landscape',
            'landscape-left',
            'landscape-right'
          ]}
          images={selectedItems}
          onRequestClose={() => setIsComparing(false)}
        />
      )}
    </Modal>
  );
};

export default ImageGalleryModal;
